import React, { useState } from "react";
import axios from "axios";
import { useLang } from "@/i18n/LanguageContext";
import { PageShell } from "@/components/PageShell";
import { Loader2, CheckCircle2, MessageCircle, MessageSquare, Mail, Info, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHONE_DISPLAY = "+32 498 73 79 19";
const WHATSAPP_URL = "https://wa.me/32498737919";
const SMS_URL = "sms:+32498737919";

const CHANNELS = (isFr) => [
  {
    key: "whatsapp",
    priority: 1,
    label: "WhatsApp Business",
    href: WHATSAPP_URL,
    cta: isFr ? "Ouvrir WhatsApp" : "Open WhatsApp",
    desc: isFr
      ? "Le canal le plus rapide. Réponse écrite, idéal pour échanger photos, documents et vocaux."
      : "The fastest channel. Written replies, ideal for sharing photos, documents and voice notes.",
    icon: MessageCircle,
    color: "#25D366",
    external: true,
  },
  {
    key: "sms",
    priority: 2,
    label: "SMS",
    href: SMS_URL,
    cta: isFr ? "Envoyer un SMS" : "Send an SMS",
    desc: isFr
      ? "Pour une question courte sans installer WhatsApp."
      : "For a short question without installing WhatsApp.",
    icon: MessageSquare,
    color: "#00F0FF",
    external: false,
  },
];

export default function Contact() {
  const { t, locale } = useLang();
  const isFr = locale === "fr";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false, err: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, err: "" });
    try {
      await axios.post(`${API}/contact`, { ...form, locale });
      setStatus({ loading: false, ok: true, err: "" });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, err: err.response?.data?.detail || t("forms.error") });
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const channels = CHANNELS(isFr);

  return (
    <PageShell
      testId="page-contact"
      title={t("nav.contact")}
      subtitle={isFr
        ? "L'écrit est privilégié. Choisissez le canal qui vous correspond — chaque demande est lue avec attention."
        : "Written contact is preferred. Pick the channel that suits you — every request is read with care."}
    >
      {/* Priority channels */}
      <div className="grid md:grid-cols-2 gap-5 mb-8" data-testid="contact-priority-channels">
        {channels.map((c) => {
          const Icon = c.icon;
          return (
            <a
              key={c.key}
              href={c.href}
              {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="glass glow-border rounded-2xl p-6 group hover:translate-y-[-2px] transition-transform"
              data-testid={`contact-channel-${c.key}`}
              aria-label={`${c.label} — ${c.cta}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                  style={{ background: `${c.color}1a`, border: `1px solid ${c.color}55` }}
                >
                  <Icon size={18} style={{ color: c.color }} />
                </span>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  {isFr ? "Priorité" : "Priority"} {c.priority}
                </span>
              </div>
              <p className="font-display text-lg text-amber-glow mb-2">{c.label}</p>
              <p className="text-xs text-slate-400 leading-relaxed mb-4 min-h-[3.5em]">{c.desc}</p>
              <span className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-[#00F0FF] group-hover:text-[#D4AF37] transition-colors">
                {c.cta} →
              </span>
            </a>
          );
        })}
      </div>

      {/* Phone display non-clickable */}
      <div className="glass rounded-2xl px-6 py-5 mb-10 border border-[#1E293B] flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="contact-phone-display">
        <p className="font-mono-tech text-lg text-slate-100 tracking-wider select-all">{PHONE_DISPLAY}</p>
        <p className="text-xs text-slate-500 italic">
          {isFr
            ? "Tél : +32 498 73 79 19 — Privilégiez WhatsApp ou SMS (je suis peut-être en consultation). Merci."
            : "Tel: +32 498 73 79 19 — Please favour WhatsApp or SMS (I may be in consultation). Thank you."}
        </p>
      </div>

      {/* Email form (option 4) */}
      <div className="mb-3 flex items-baseline gap-3">
        <h2 className="font-display text-xl text-cyan-glow">
          {isFr ? "Option 4 — Formulaire écrit (email)" : "Option 4 — Written form (email)"}
        </h2>
        <span className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500">
          {isFr ? "Réponse sous 48 h" : "Reply within 48 h"}
        </span>
      </div>
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-5 max-w-3xl glass rounded-2xl p-8 glow-border" data-testid="contact-form">
        <div>
          <label className="label-overline block mb-2">{t("forms.name")}</label>
          <input className="input-tech" required value={form.name} onChange={update("name")} data-testid="contact-name" />
        </div>
        <div>
          <label className="label-overline block mb-2">{t("forms.email")}</label>
          <input className="input-tech" type="email" required value={form.email} onChange={update("email")} data-testid="contact-email" />
        </div>
        <div>
          <label className="label-overline block mb-2">{t("forms.phone")}</label>
          <input className="input-tech" value={form.phone} onChange={update("phone")} data-testid="contact-phone" />
        </div>
        <div>
          <label className="label-overline block mb-2">{t("forms.subject")}</label>
          <input className="input-tech" value={form.subject} onChange={update("subject")} data-testid="contact-subject" />
        </div>
        <div className="md:col-span-2">
          <label className="label-overline block mb-2">{t("forms.message")}</label>
          <textarea className="input-tech min-h-[140px]" required value={form.message} onChange={update("message")} data-testid="contact-message" />
        </div>
        <div className="md:col-span-2 flex items-center gap-4 flex-wrap">
          <button type="submit" className="btn-primary" disabled={status.loading} data-testid="contact-submit">
            {status.loading ? <Loader2 size={14} className="animate-spin" /> : null}
            {status.loading ? t("forms.submitting") : t("forms.submit")}
          </button>
          <a
            href="mailto:contact@facow360.com"
            className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 hover:text-[#00F0FF] inline-flex items-center gap-2"
            data-testid="contact-email-direct"
          >
            <Mail size={12} /> contact@facow360.com
          </a>
          {status.ok && (
            <span className="inline-flex items-center gap-2 text-sm text-[#00F0FF]" data-testid="contact-success">
              <CheckCircle2 size={16} /> {t("forms.success")}
            </span>
          )}
          {status.err && <span className="text-sm text-red-400" data-testid="contact-error">{status.err}</span>}
        </div>
      </form>

      <div className="mt-10 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <MapPin size={12} className="text-[#D4AF37]" />
        <span>20, quai de Lorraine — 11100 Narbonne, France</span>
      </div>
      <p className="mt-6 text-xs text-slate-500">{t("disclaimer")}</p>
    </PageShell>
  );
}
